import {
  Component,
  ChangeDetectionStrategy,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { SoSelectUI } from '../so-select/so-select.component';
import { SoSelectItem } from '../interfaces/select.interfaces';
import { SoTableTokenPaginationEvent, SoTableTokenPaginationState } from '../so-table/so-table.interfaces';

@Component({
  selector: 'so-token-pagination-ui',
  standalone: true,
  imports: [CommonModule, SoSelectUI],
  templateUrl: './so-token-pagination.component.html',
  styleUrl: './so-token-pagination.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SoTokenPaginationUI {
  tokenPaginationState = input<SoTableTokenPaginationState>({
    pageCounter: 0,
    pageSize: 10,
    nextToken: '',
    prevToken: '',
  });
  itemsPerPage = input<number>(15);
  pageSizeOptions = input<number[]>([10, 25, 50, 100]);
  totalItems = input<number>(0);

  nextPageRequested = output<SoTableTokenPaginationEvent>();
  previousPageRequested = output<SoTableTokenPaginationEvent>();
  pageSizeChanged = output<number>();

  protected internalPageSize = signal<number | null>(null);

  protected pageSizeSelectItems = computed<SoSelectItem[]>(() =>
    this.pageSizeOptions().map(size => ({
      key: size,
      value: `${size}`,
    }))
  );

  protected effectivePageSize = computed(() => {
    const local = this.normalizePageSize(this.internalPageSize());
    if (local) return local;

    const external = this.normalizePageSize(this.tokenPaginationState().pageSize);
    if (external) return external;

    const fallback = this.normalizePageSize(this.itemsPerPage());
    if (fallback) return fallback;

    const firstOption = this.normalizePageSize(this.pageSizeOptions()[0]);
    return firstOption ?? null;
  });

  constructor() {
    effect(() => {
      const size = this.normalizePageSize(this.tokenPaginationState().pageSize);
      this.internalPageSize.set(size);
    }, {  });
  }

  onNextTokenPage(): void {
    const state = this.tokenPaginationState();
    if (state.nextToken) {
      this.nextPageRequested.emit({
        token: state.nextToken,
        pageSize: state.pageSize
      });
    }
  }

  onPreviousTokenPage(): void {
    const state = this.tokenPaginationState();
    if (state.prevToken) {
      this.previousPageRequested.emit({
        token: state.prevToken,
        pageSize: state.pageSize
      });
    }
  }

  onPageSizeChange(size: number | string | boolean | null): void {
    const normalized = this.normalizePageSize(size);
    if (normalized === null) return;

    this.pageSizeChanged.emit(normalized);
    this.internalPageSize.set(normalized);
  }

  isNextTokenDisabled(): boolean {
    return !this.tokenPaginationState().nextToken;
  }

  isPrevTokenDisabled(): boolean {
    return !this.tokenPaginationState().prevToken;
  }

  private normalizePageSize(value: number | string | boolean | null | undefined): number | null {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return parsed;
  }
}
